/**
 * LUNA Coding Tutor - Store
 * 원본: state.js의 전역 상태 관리를 Zustand로 재구현
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { produce } from 'immer';

// ============================================
// 상수 정의 (원본 state.js와 동일)
// ============================================

export const APP_STATES = {
  IDLE: 'IDLE',                                   // 초기 상태
  AWAITING_LEARNING_TYPE: 'AWAITING_LEARNING_TYPE', // 학습 유형 선택 대기
  AWAITING_PATH_SELECTION: 'AWAITING_PATH_SELECTION', // [신규] 학습 경로 상담 선택 대기
  AWAITING_TOPIC_REFINEMENT: 'AWAITING_TOPIC_REFINEMENT', // 주제 세분화 대기
  CURRICULUM_PROPOSED: 'CURRICULUM_PROPOSED',     // 커리큘럼 제안됨 (시작 확인 대기)
  ROADMAP_GENERATED: 'ROADMAP_GENERATED',         // [신규] 로드맵 생성 완료 (버튼 선택 대기)
  CURRICULUM_GENERATED: 'CURRICULUM_GENERATED',   // [신규] 커리큘럼 생성 완료 (버튼 선택 대기)
  CURRICULUM_CHOICE: 'CURRICULUM_CHOICE',         // 커리큘럼 선택 단계 (Deprecated?)
  LESSON_EXPLAINED: 'LESSON_EXPLAINED',           // 레슨 설명 완료 (문제 풀기 전)
  CONCEPT_VIEW: 'CONCEPT_VIEW',                   // 개념 학습 중
  PROBLEM_SOLVING: 'PROBLEM_SOLVING',             // 문제 풀이 중
  CHAPTER_COMPLETE: 'CHAPTER_COMPLETE',           // 단원 완료
  AWAITING_REGENERATE_INPUT: 'AWAITING_REGENERATE_INPUT', // 재생성 방향 입력 대기
};

export const CHAPTER_STAGES = {
  IDLE: 'IDLE',
  CONCEPT_LEARNING: 'CONCEPT_LEARNING', // 개념 학습
  BASIC_EXERCISE: 'BASIC_EXERCISE',     // 개념 다지기
  MAIN_PROJECT: 'MAIN_PROJECT',         // 실전 문제
  REVIEW: 'REVIEW',                     // 복습/정리
};

export const LEARNING_TYPES = {
  ROADMAP: 'roadmap',       // 장기 로드맵
  CURRICULUM: 'curriculum', // 단기 커리큘럼
};

// ============================================
// Zustand 스토어 생성
// ============================================

export const useLunaStore = create(
  devtools(
    persist(
      (set, get) => ({
        // ============================================
        // State (원본 state 객체 매핑)
        // ============================================

        // 앱 상태
        appState: APP_STATES.IDLE,
        currentChapterStage: CHAPTER_STAGES.IDLE,
        intendedTopic: null,

        // 커리큘럼 및 레슨 데이터
        currentCurriculum: null,     // 전체 커리큘럼
        currentStepIndex: 0,         // 커리큘럼 단계 인덱스

        currentLessonPlan: null,     // 현재 레슨 플랜 (steps 포함)
        currentLessonStepIndex: 0,   // 레슨 내부 스텝 인덱스

        problemJSON: null,           // 현재 문제 데이터

        // 채팅 및 상황
        chatHistory: [],             // 채팅 기록
        isAwaitingResponse: false,   // AI 응답 대기 중

        // UI 상태
        sidebarOpen: true,           // 사이드바 열림 여부

        // [신규] 2.0 추가 상태
        lessonCompleted: false,      // 레슨 완료 여부
        isRoadmapMode: false,        // 로드맵 모드 활성화 여부
        roadmapContext: null,        // 로드맵 컨텍스트 { pIdx, tIdx, topic... }
        currentRoadmap: null,        // [신규] 현재 생성된 로드맵 (카드 표시용)
        curriculumProgress: {},      // 커리큘럼 진행 상황 (로드맵 모드용)
        pendingRegenerateType: null, // 재생성 대기 타입 ('roadmap' | 'curriculum')
        pendingNextStepOffer: false, // 다음 단계 제안 대기 중
        newlyUnlockedTopic: null,    // [New] 방금 잠금 해제된 토픽 정보 (애니메이션용)
        levelUpData: null,           // [New] 전역 레벨업 데이터 { level }
        needsCurriculumGeneration: false, // [New] 로드맵 토픽 전환 시 커리큘럼 생성 필요 플래그
        chatInputLocked: false,      // 채팅 입력 잠금
        chatInputPlaceholder: '',    // 채팅 입력 잠금 메시지
        chatInputValue: '',          // 채팅 입력 값 (Welcome Screen과 연동)

        // 상태 플래그
        isLoading: false,
        loadingMessage: '',

        // 에디터 상태
        lastCodeSnapshot: null,

        // 사용자 세션 상태
        username: null,
        nickname: null, // [New] 닉네임 추가
        isAdmin: false,
        userId: null,
        userLevel: 1,
        userXP: 0,
        userStreak: 0,
        requiredXP: 100,

        // ============================================
        // Actions
        // ============================================

        // 사용자 로그인/로그아웃 처리
        setUserSession: (userData) => set({
          username: userData.username,
          nickname: userData.nickname || null, // [New] 닉네임 저장
          isAdmin: userData.is_admin || false,
          userId: userData.user_id || null,
          userLevel: userData.level || 1,
          userXP: userData.total_xp || 0,
          userStreak: userData.streak || 0,
          requiredXP: userData.required_xp || 100,
        }),

        clearUserSession: () => {
          // 1. 앱 상태 초기화 (채팅, 커리큘럼 등)
          get().resetAll();

          // 2. 사용자 세션 정보 제거
          set({
            username: null,
            nickname: null, // [New] 닉네임 초기화
            isAdmin: false,
            userId: null,
            userLevel: 1,
            userXP: 0,
            userStreak: 0
          });
        },

        // [New] Individual User Stats Setters
        setUserXP: (xp) => set({ userXP: xp }),
        setUserLevel: (level) => set({ userLevel: level }),
        setUserStreak: (streak) => set({ userStreak: streak }),
        setRequiredXP: (xp) => set({ requiredXP: xp }),

        // 앱 상태 변경
        setAppState: (state) => set({ appState: state }),
        setChatInputValue: (val) => set({ chatInputValue: val }),
        setChapterStage: (stage) => set({ currentChapterStage: stage }),
        setIntendedTopic: (topic) => set({ intendedTopic: topic }),

        // 로딩 상태 제어
        setLoading: (isLoading, message = '') => set({ isLoading, loadingMessage: message }),

        // 커리큘럼 설정
        setCurriculum: (curriculum) => set({ currentCurriculum: curriculum }),
        setStepIndex: (index) => set({ currentStepIndex: index }),
        setCurriculumProgress: (progress) => set({ curriculumProgress: progress }),
        setCurrentRoadmap: (roadmap) => set({ currentRoadmap: roadmap }),

        // 레슨 설정
        setLessonPlan: (plan) => set({ currentLessonPlan: plan }),
        setLessonStepIndex: (index) => set({ currentLessonStepIndex: index }),
        setLessonCompleted: (completed) => set({ lessonCompleted: completed }),

        // 문제 설정
        setProblem: (problem) => set({ problemJSON: problem }),

        // 채팅 관련
        addChatMessage: (roleOrMsg, contentOrSkipSave, skipSaveParam) => {
          // 오버로딩 지원: (msgObject, skipSave) 또는 (role, content, skipSave)
          let newMsg;
          let skipSave = false;

          if (typeof roleOrMsg === 'object') {
            newMsg = { ...roleOrMsg, timestamp: new Date().toISOString() };
            skipSave = !!contentOrSkipSave;
          } else {
            newMsg = {
              role: roleOrMsg,
              content: contentOrSkipSave,
              timestamp: new Date().toISOString()
            };
            skipSave = !!skipSaveParam;
          }

          set(produce((state) => {
            state.chatHistory.push(newMsg);
          }));
        },

        clearChatHistory: () => set({ chatHistory: [] }),

        setChatInputLocked: (locked, placeholder = '') => set({
          chatInputLocked: locked,
          chatInputPlaceholder: placeholder
        }),

        // 로드맵 관련
        setRoadmapMode: (isActive, context = null) => set({
          isRoadmapMode: isActive,
          roadmapContext: context
        }),
        setRoadmapContext: (context) => set({ roadmapContext: context }),
        setIsRoadmapMode: (isActive) => set({ isRoadmapMode: isActive }), // [New] 명시적 Setter
        suspendRoadmap: () => set({ isRoadmapMode: false }), // 로드맵 일시 정지 (context/roadmap 데이터 유지)
        setChatHistory: (history) => set({ chatHistory: history || [] }), // [New] 전체 히스토리 설정

        // 플래그 설정
        setPendingRegenerateType: (type) => set({ pendingRegenerateType: type }),
        setPendingNextStepOffer: (pending) => set({ pendingNextStepOffer: pending }),
        setNewlyUnlockedTopic: (topic) => set({ newlyUnlockedTopic: topic }), // [New]
        setLevelUpData: (data) => set({ levelUpData: data }), // [New]
        setNeedsCurriculumGeneration: (needed) => set({ needsCurriculumGeneration: needed }), // [New] 로드맵 토픽 전환 플래그

        // 에디터 스냅샷
        setLastCodeSnapshot: (code) => set({ lastCodeSnapshot: code }),

        // 사이드바 토글
        toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),

        // 초기화 (Reset)
        resetAll: (initialChatHistory = [], options = {}) => set((state) => {
          const keepRoadmap = options.keepRoadmap || false;
          return {
            appState: APP_STATES.IDLE,
            currentChapterStage: CHAPTER_STAGES.IDLE,
            intendedTopic: null,
            currentCurriculum: null,
            currentStepIndex: 0,
            currentLessonPlan: null,
            currentLessonStepIndex: 0,
            problemJSON: null,
            chatHistory: initialChatHistory,
            isAwaitingResponse: false,
            lessonCompleted: false,

            // [Fix] 로드맵 유지 옵션 적용
            isRoadmapMode: keepRoadmap ? state.isRoadmapMode : false,
            roadmapContext: keepRoadmap ? state.roadmapContext : null,

            curriculumProgress: {},
            pendingRegenerateType: null,
            pendingNextStepOffer: false,
            needsCurriculumGeneration: false,
            chatInputLocked: false,
            chatInputPlaceholder: '',
            lastCodeSnapshot: null,

            // 사용자 세션 정보는 유지 (username 등)
          };
        }),





        // 페르소나 (임시 - 실제 구현은 별도 컴포넌트나 컨텍스트에서 처리될 수 있음)
        tutorExpression: 'default',
        setTutorExpression: (expr) => set({ tutorExpression: expr }),

        // 네비게이션 헬퍼
        goToNextLessonStep: () => {
          const { currentLessonPlan, currentLessonStepIndex } = get();
          if (!currentLessonPlan?.steps) return false;

          const nextIndex = currentLessonStepIndex + 1;
          if (nextIndex < currentLessonPlan.steps.length) {
            set({ currentLessonStepIndex: nextIndex });
            return true;
          } else {
            set({ lessonCompleted: true });
            return false;
          }
        },

        goToPrevLessonStep: () => {
          const { currentLessonStepIndex } = get();
          if (currentLessonStepIndex > 0) {
            set({ currentLessonStepIndex: currentLessonStepIndex - 1 });
            return true;
          }
          return false;
        },

        goToNextCurriculumStep: () => {
          const { currentCurriculum, currentStepIndex } = get();
          if (!currentCurriculum?.curriculum) return { isComplete: false };

          const nextIndex = currentStepIndex + 1;
          if (nextIndex < currentCurriculum.curriculum.length) {
            set({
              currentStepIndex: nextIndex,
              currentLessonPlan: null,
              currentLessonStepIndex: 0,
              lessonCompleted: false
            });
            // [Fix] nextIndex도 반환하여 stale closure 문제 해결
            return { isComplete: false, nextStep: currentCurriculum.curriculum[nextIndex], nextIndex };
          } else {
            return { isComplete: true };
          }
        },

        // 현재 레슨 플랜 설정 (Steps 포함)
        setCurrentLessonPlan: (plan) => set({ currentLessonPlan: plan }),

        // 현재 레슨 스텝 인덱스 설정
        setCurrentLessonStepIndex: (index) => set((state) => ({
          currentLessonStepIndex: typeof index === 'function' ? index(state.currentLessonStepIndex) : index
        })),

        // 현재 커리큘럼 설정
        setCurrentCurriculum: (curriculum) => set({ currentCurriculum: curriculum }),

        // 현재 커리큘럼 스텝 인덱스 설정
        setCurrentStepIndex: (index) => set((state) => ({
          currentStepIndex: typeof index === 'function' ? index(state.currentStepIndex) : index
        })),
      }),
      {
        name: 'luna-storage', // localStorage key
        partialize: (state) => {
          if (!state.username) return {}; // [Fix] 게스트 세션은 저장하지 않음 (새로고침 시 초기화)
          return {
            // 저장할 상태만 선별
            username: state.username,
            nickname: state.nickname, // [New] 닉네임 영속화
            isAdmin: state.isAdmin,
            userId: state.userId,
            userLevel: state.userLevel,
            userXP: state.userXP,
            userStreak: state.userStreak,
            appState: state.appState,
            currentChapterStage: state.currentChapterStage,
            intendedTopic: state.intendedTopic,
            currentCurriculum: state.currentCurriculum,
            currentStepIndex: state.currentStepIndex,
            currentLessonPlan: state.currentLessonPlan,
            currentLessonStepIndex: state.currentLessonStepIndex,
            chatHistory: state.chatHistory,
            lessonCompleted: state.lessonCompleted,
            isRoadmapMode: state.isRoadmapMode,
            roadmapContext: state.roadmapContext,
            problemJSON: state.problemJSON,
            lastCodeSnapshot: state.lastCodeSnapshot,
            // [Fix] 추가 영속화
            currentRoadmap: state.currentRoadmap,
            newlyUnlockedTopic: state.newlyUnlockedTopic, // [New]
            needsCurriculumGeneration: state.needsCurriculumGeneration, // [Fix] 로드맵 전환 플래그 영속화
          };
        },
      }
    )
  )
);

export default useLunaStore;
